/**
 * Campaign management API — time-boxed ASX sales campaigns.
 *
 * Backend routes (operator-level): /campaigns, /campaigns/{id},
 * /campaigns/{id}/close, /campaigns/{id}/members, /campaigns/{id}/roster
 * See docs/wip/assayer-stream/CAMPAIGN_SYSTEM_PLAN.md
 */

import { apiClient } from './config.js';

export interface Campaign {
  campaign_id: string;
  name: string;
  status: 'active' | 'closed';
  event_name: string | null;
  event_date: string | null;
  starts_at: string | null;
  ends_at: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  member_count?: number;
  notify_enabled?: boolean;
  notify_cadence?: 'immediate' | 'daily_digest';
  notify_last_sent_at?: string | null;
}

export interface CampaignRecipient {
  recipient_id: string;
  campaign_id: string;
  email: string;
  name: string | null;
  added_by: string | null;
  added_at: string;
}

export interface CampaignNotificationLogEntry {
  log_id: string;
  campaign_id: string;
  article_id: string;
  recipient_email: string;
  status: 'sent' | 'test_sent' | 'failed';
  mailjet_message_id: string | null;
  error_message: string | null;
  sent_at: string;
}

export interface CampaignMember {
  entity_id: string;
  canonical_identifier: string;
  display_name: string;
  approval_status: string;
  entity_type: string;
  exchange: string | null;
  cynapse_entity_id: string | null;
  added_at: string;
  added_by: string | null;
}

export interface CampaignDetail extends Campaign {
  members: CampaignMember[];
}

export interface CampaignCreate {
  name: string;
  event_name?: string | null;
  event_date?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  notes?: string | null;
}

export interface RosterUploadSummary {
  companies_added: number;
  other_exchange_added: number;
  private_companies_added: number;
  contacts_ingested: number;
  contacts_needs_review: number;
  contacts_failed: number;
  rows_skipped: number;
  rows_parsed: number;
  skipped_reasons: Array<{ company: string | null; reason: string }>;
}

export async function listCampaigns(status?: 'active' | 'closed'): Promise<Campaign[]> {
  const res = await apiClient.get<{ campaigns: Campaign[]; count: number }>('/campaigns', {
    params: status ? { status } : undefined,
  });
  return res.data.campaigns;
}

export async function getCampaign(campaignId: string): Promise<CampaignDetail> {
  const res = await apiClient.get<CampaignDetail>(`/campaigns/${campaignId}`);
  return res.data;
}

export async function createCampaign(body: CampaignCreate): Promise<Campaign> {
  const res = await apiClient.post<Campaign>('/campaigns', body);
  return res.data;
}

export async function updateCampaign(
  campaignId: string,
  body: Partial<CampaignCreate>,
): Promise<Campaign> {
  const res = await apiClient.patch<Campaign>(`/campaigns/${campaignId}`, body);
  return res.data;
}

export async function closeCampaign(campaignId: string): Promise<Campaign> {
  const res = await apiClient.post<Campaign>(`/campaigns/${campaignId}/close`);
  return res.data;
}

export async function reopenCampaign(campaignId: string): Promise<Campaign> {
  const res = await apiClient.post<Campaign>(`/campaigns/${campaignId}/reopen`);
  return res.data;
}

export async function addCampaignMember(
  campaignId: string,
  body: { entity_id?: string; asx_code?: string; company_name?: string },
): Promise<{ entity_id: string; created: boolean }> {
  const res = await apiClient.post(`/campaigns/${campaignId}/members`, body);
  return res.data;
}

export async function removeCampaignMember(
  campaignId: string,
  entityId: string,
): Promise<void> {
  await apiClient.delete(`/campaigns/${campaignId}/members/${entityId}`);
}

export async function listRecipients(campaignId: string): Promise<CampaignRecipient[]> {
  const res = await apiClient.get<{ recipients: CampaignRecipient[]; count: number }>(
    `/campaigns/${campaignId}/recipients`,
  );
  return res.data.recipients;
}

export async function addRecipient(
  campaignId: string,
  body: { email: string; name?: string },
): Promise<{ recipient: CampaignRecipient | null; created: boolean }> {
  const res = await apiClient.post(`/campaigns/${campaignId}/recipients`, body);
  return res.data;
}

export async function removeRecipient(
  campaignId: string,
  recipientId: string,
): Promise<void> {
  await apiClient.delete(`/campaigns/${campaignId}/recipients/${recipientId}`);
}

export async function updateNotifyConfig(
  campaignId: string,
  body: { notify_enabled?: boolean; notify_cadence?: 'immediate' | 'daily_digest' },
): Promise<Campaign> {
  const res = await apiClient.patch<Campaign>(`/campaigns/${campaignId}/notify-config`, body);
  return res.data;
}

export async function sendTestNotification(
  campaignId: string,
): Promise<{ recipients: string[]; sent: number; failed: number; error?: string }> {
  const res = await apiClient.post(`/campaigns/${campaignId}/notify-test`);
  return res.data;
}

export async function getNotificationLog(
  campaignId: string,
): Promise<CampaignNotificationLogEntry[]> {
  const res = await apiClient.get<{ log: CampaignNotificationLogEntry[]; count: number }>(
    `/campaigns/${campaignId}/notification-log`,
  );
  return res.data.log;
}

export async function uploadRoster(
  campaignId: string,
  file: File,
): Promise<RosterUploadSummary> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiClient.post<RosterUploadSummary>(
    `/campaigns/${campaignId}/roster`,
    formData,
    {
      // Let axios set the multipart boundary; roster parse + ingest can be slow.
      headers: { 'Content-Type': undefined as unknown as string },
      timeout: 300000,
    },
  );
  return res.data;
}
